import type { BusinessCalendar } from "@helpdesk/sla";

import { Avatar } from "./ui/Avatar";
import { ChannelIcon } from "./ui/ChannelIcon";
import { ResizeHandle } from "./ui/ResizeHandle";
import { cn } from "./ui/cn";
import { ListEnd } from "./list/ListEnd";
import { SlaIndicator } from "./SlaClock";
import { StatusBadge } from "./StatusBadge";
import { assigneeOf } from "../lib/agents";
import { usePaneSize } from "../lib/pane-size";
import { useLiveSla } from "../lib/sla";
import { absoluteTime, relativeTime } from "../lib/time";
import type { Pipeline, TicketNote, TicketSummary, User } from "../types";

/**
 * The narrow conversation list shown down the left of the preview layout.
 *
 * Purely presentational: it renders whatever the current view fetched, so the
 * sidebar view and the filter bar govern both layouts identically and opening a
 * ticket cannot change which tickets are on screen.
 */
export function PreviewList({
  tickets,
  pipelines,
  users,
  calendar,
  selectedId,
  unread,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  onSelect,
}: {
  tickets: TicketSummary[];
  pipelines: Pipeline[];
  /** The agent list, for the assignee's chosen avatar tint. */
  users: User[];
  calendar: BusinessCalendar;
  selectedId: number | null;
  /** Ticket ids with customer mail the signed-in agent has not seen. */
  unread: Set<number>;
  loading: boolean;
  /** Another page exists behind the rows currently held. */
  hasMore: boolean;
  /** That next page is in flight. */
  loadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (id: number) => void;
}) {
  /*
   * How wide this column is, dragged and remembered. The old `w-96` is the
   * default rather than the rule: a subject line that fits on a 34-inch
   * monitor is truncated to uselessness on a laptop, and which of those an
   * agent is sitting at is not something a stylesheet can know.
   */
  const width = usePaneSize("pulse.pane.preview", 384, { min: 240, max: 720 });

  return (
    <div className="flex min-h-0 shrink-0">
      <div
        style={{ width: width.size }}
        className="flex min-w-0 shrink-0 flex-col bg-app"
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          {tickets.length === 0 ? (
            <p className="p-4 text-sm text-muted">
              {loading ? "Loading tickets…" : "No tickets match this view."}
            </p>
          ) : (
            <ul>
              {tickets.map((ticket) => (
                <li key={ticket.id}>
                  <PreviewRow
                    ticket={ticket}
                    pipelines={pipelines}
                    users={users}
                    calendar={calendar}
                    selected={ticket.id === selectedId}
                    unread={unread.has(ticket.id)}
                    onSelect={onSelect}
                  />
                </li>
              ))}
              <li>
                <ListEnd
                  hasMore={hasMore}
                  loading={loadingMore}
                  onReach={onLoadMore}
                />
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* The column's right border, made draggable. */}
      <ResizeHandle
        axis="x"
        label="Ticket list"
        size={width.size}
        min={240}
        max={720}
        onChange={width.setSize}
        onReset={width.reset}
      />
    </div>
  );
}

function PreviewRow({
  ticket,
  pipelines,
  users,
  calendar,
  selected,
  unread,
  onSelect,
}: {
  ticket: TicketSummary;
  pipelines: Pipeline[];
  users: User[];
  calendar: BusinessCalendar;
  selected: boolean;
  unread: boolean;
  onSelect: (id: number) => void;
}) {
  const sla = useLiveSla(ticket, pipelines, calendar);
  const pipeline = pipelines.find((entry) => entry.id === ticket.pipelineId);

  return (
    <button
      type="button"
      onClick={() => onSelect(ticket.id)}
      className={cn(
        "block w-full border-b border-line px-3 py-2 text-left",
        "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40",
        selected ? "bg-brand-50 dark:bg-brand-950/40" : "hover:bg-surface-2",
      )}
    >
      <div className="flex items-baseline gap-2">
        {/*
          How it reached us, leading the line that says who it is from -- the
          same pairing the table makes with its Channel column, which sits
          between From and Subject. Not down in the marker row below with the
          status, the SLA and the pipeline: those say whether a ticket needs
          somebody, and this says where it came from, which is the timestamp's
          kind of fact rather than theirs.

          `self-center` because the row is baseline-aligned for the text, and
          an icon's baseline is its bottom edge -- left to it the glyph hangs
          below the name it belongs to.
        */}
        <ChannelIcon
          type={ticket.channelType}
          name={ticket.channelName}
          className="size-3.5 shrink-0 self-center text-muted"
        />

        {/* Unread is carried by weight alone here. A coloured dot would be the
            fifth marker competing in a column that already shows status, SLA
            and pipeline. */}
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-sm text-content",
            unread && "font-semibold",
          )}
        >
          {ticket.companyName ?? ticket.contactName ?? "Unknown"}
        </span>
        <span
          className={cn(
            "shrink-0 text-[11px]",
            unread ? "font-semibold text-content" : "text-faint",
          )}
          title={ticket.lastMessageAt}
        >
          {relativeTime(ticket.lastMessageAt)}
        </span>
      </div>

      <div
        title={ticket.subject}
        className={cn(
          "mt-0.5 truncate text-sm text-content",
          unread && "font-semibold",
        )}
      >
        {ticket.subject}
      </div>

      {/* The snippet stays here: this column is tall and narrow, so a second
          line of context earns its space in a way it does not in the table. */}
      {ticket.lastMessagePreview ? (
        <p className="mt-1 line-clamp-2 text-xs font-normal text-muted">
          {ticket.lastMessagePreview}
        </p>
      ) : null}

      {/*
        The newest internal note, in amber.

        Notes here are mostly somebody recording what a ticket is waiting on,
        and until now the only way to find out was to open every ticket in
        turn. One coloured line per row turns that into a glance -- which is
        also what makes it answerable when somebody asks which of these to do
        first.

        Amber and indented rather than another badge in the marker row below:
        that row says whether a ticket needs somebody, and this says what a
        colleague wrote. Colour rather than a heading, because the row has no
        width to spend on the word "Note".

        Deliberately below the customer's snippet and not instead of it. The
        note is context on the conversation, and a row that showed only the
        note would hide the thing the note is about.
      */}
      {ticket.lastNote ? (
        <p
          title={noteTitle(ticket.lastNote)}
          className={cn(
            /* One line at the timestamp's size, which is the smallest thing
               on the row. A note is a marker -- it says this ticket has one
               and roughly what about -- and the ticket itself is where you
               read it, so it should not out-weigh the customer's own words
               sitting above it. */
            "mt-1 line-clamp-1 rounded-sm border-l-2 py-px pl-1.5 text-[11px] font-normal leading-snug",
            /* The same amber the note bubble and the note composer already
               use, so a note is the same colour everywhere it appears. */
            "border-amber-300 bg-amber-50 text-amber-900",
            "dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
          )}
        >
          {ticket.lastNote.authorName ? (
            /* First name only. On one line a full name and a surname would
               spend most of it saying who, leaving no room for what. */
            <span className="font-medium">
              {ticket.lastNote.authorName.split(" ")[0]}:{" "}
            </span>
          ) : null}
          {ticket.lastNote.snippet}
        </p>
      ) : null}

      <div className="mt-1 flex items-center gap-1.5">
        {/* Same initials circle as the table, so an agent is recognisable
            wherever they appear. Only shown when assigned -- an empty dashed
            ring on every unassigned row would be noise in a column this dense. */}
        {ticket.assigneeId !== null ? (
          <Avatar
            size="xs"
            agent={assigneeOf(users, ticket.assigneeId, ticket.assigneeName)}
          />
        ) : null}
        <StatusBadge status={ticket.status} />
        {sla.level !== null ? <SlaIndicator sla={sla} /> : null}
        {pipeline ? (
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: pipeline.color }}
            title={pipeline.name}
          />
        ) : null}
        <span className="ml-auto shrink-0 text-[11px] text-faint tabular-nums">
          #{ticket.id}
        </span>
      </div>
    </button>
  );
}

/**
 * What the note line says on hover: who wrote it and when.
 *
 * The line itself carries neither. It is one clamped line in a column an agent
 * has usually dragged narrow, and a date on the front would cost most of the
 * words -- so the row shows what was said, and the tooltip answers the question
 * the row provokes.
 */
function noteTitle(note: TicketNote): string {
  const who = note.authorName ?? "Somebody";
  return `Note from ${who}, ${absoluteTime(note.at)}`;
}
